// ** React Imports
import { createContext, useRef, useState, useCallback } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Config
import authConfig from 'src/configs/auth'
import { filterAudiencesUrl, filterUrl, generateSegmentQueryUrl, segmentsListUrl } from 'src/configs/api'

import { predefinedSegments } from '../configs/api'

import { AuthContext } from './AuthContext'
import { useAPI } from 'src/utils/api'
import { authStorage } from 'src/utils/authStorage'
import { useToast } from 'src/@core/components/custom-Toast/customToast'
// ** Defaults
const defaultProvider = {
  tagListData: [],
  setTagListData: () => null,

  segmentListData: [],
  setSegmentListData: () => null,
  segmentName: '',
  setSegmentName: () => null,
  segmentQueryPrompt: '',
  setSegmentQueryPrompt: () => null,
  segmentQuerySql: '',
  setSegmentQuerySql: () => null,
  segmentQueryJson: null,
  isResponsePending: false,
  setIsResponsePending: () => null,
  setSegmentQueryJson: () => null,
  selectedSegment: {},
  setSelectedSegment: () => null,
  selectedSegmentID: '',
  setSelectedSegmentID: () => null,
  queryData: '',
  setQueryData: () => null,
  segmentSelectionData: [],
  setSegmentSelectionData: () => null,
  predefinedSegmentID: '',
  setPredefinedSegmentID: () => null,
  executeQueryData: '',
  setExecuteQueryData: () => null,
  segmentType: 'dynamic',
  setSegmentType: () => null,
  totalSegments: 0,
  setTotalSegments: () => null,
  tags: [],
  setTags: () => null,
  segmentFilterList: [],
  setSegmentFilterList: () => null,
  segmentOtherFilterList: [],
  setSegmentOtherFilterList: () => null,
  hasTagFilter: false,
  setHasTagFilter: () => null,
  isExecuting: false,
  setIsExecuting: () => null
}

const SegmentContext = createContext(defaultProvider)

const SegmentProvider = ({ children }) => {
  const [tagListData, setTagListData] = useState(defaultProvider.tagListData)

  const [segmentListData, setSegmentListData] = useState(defaultProvider.segmentListData)

  const [segmentSelectionData, setSegmentSelectionData] = useState(defaultProvider.segmentSelectionData)

  const [segmentName, setSegmentName] = useState(defaultProvider.segmentName)

  const [segmentQueryPrompt, setSegmentQueryPrompt] = useState(defaultProvider.segmentQueryPrompt)

  const [segmentQuerySql, setSegmentQuerySqlState] = useState(defaultProvider.segmentQuerySql)
  const setSegmentQuerySql = useCallback(val => {
    setSegmentQuerySqlState(prev => {
      if (prev === val) {return prev}
      setExecuteQueryData('')
      return val
    })
  }, [])

  const [segmentQueryJson, setSegmentQueryJsonState] = useState(defaultProvider.segmentQueryJson)
  const setSegmentQueryJson = useCallback(val => {
    setSegmentQueryJsonState(prev => {
      if (JSON.stringify(prev) === JSON.stringify(val)) {return prev}
      setExecuteQueryData('')
      return val
    })
  }, [])
  const segmentQueryJsonRef = useRef(segmentQueryJson)
  segmentQueryJsonRef.current = segmentQueryJson

  const [isResponsePending, setIsResponsePending] = useState(defaultProvider.isResponsePending)

  const [selectedSegment, setSelectedSegment] = useState(defaultProvider.selectedSegment)

  const [selectedSegmentID, setSelectedSegmentID] = useState(defaultProvider.selectedSegmentID)

  const [queryData, setQueryData] = useState(defaultProvider.queryData)

  const [predefinedSegmentID, setPredefinedSegmentID] = useState(defaultProvider.predefinedSegmentID)

  const [executeQueryData, setExecuteQueryData] = useState(defaultProvider.executeQueryData)

  const [segmentType, setSegmentType] = useState(defaultProvider.segmentType)

  const [totalSegments, setTotalSegments] = useState(defaultProvider.totalSegments)

  const [tags, setTags] = useState(defaultProvider.tags)
  const [segmentFilterList, setSegmentFilterList] = useState(defaultProvider.segmentFilterList)
  const [segmentOtherFilterList, setSegmentOtherFilterList] = useState(defaultProvider.segmentOtherFilterList)
  const [hasTagFilter, setHasTagFilter] = useState(defaultProvider.hasTagFilter)
  const [isExecuting, setIsExecuting] = useState(defaultProvider.isExecuting)

  const { showError, showSuccess } = useToast()
  // ** Hooks
  const router = useRouter()
  const api = useAPI()
  authStorage.getStoredToken(authConfig.storageTokenKeyName)

  const getSegmentNameById = segmentId => {
    const segment = (segmentListData || []).find(
      s => String(s?.segment_id || s?.segmentId || s?.id) === String(segmentId)
    )
    return segment?.name || segment?.segmentName || String(segmentId)
  }

  const getTag = async (search = '') => {
    const params = search ? { search } : {}
    const response = await api.get(filterAudiencesUrl, { params })
    const filters = response?.data?.data?.filters || []
    const tagFilter = filters.find(f => f.key === 'tags')
    setTagListData(tagFilter?.options || [])
  }

  const executeQuery = async () => {
    try {
      setIsExecuting(true)

      const inputData = {
        query_json: segmentQueryJsonRef.current
      }

      const response = await api.post(`${segmentsListUrl}/execute`, inputData)

      const executeQuery_data = response?.data?.data

      setExecuteQueryData(executeQuery_data)
    } catch {
      showError('Failed to execute segment query. Please try again.')
      return
    } finally {
      setIsExecuting(false)
    }
  }

  const getSegmentList = async (
    value,
    sortModel = [{ field: '', sort: '' }],
    page,
    pageSize,
    type = [],
    segmentTags = []
  ) => {
    try {
      const segmentPayload = {
        pagination: {
          page: page,
          pageSize: pageSize
        },
        sorting: {
          field: sortModel[0]?.field || '',
          order: sortModel[0]?.sort || ''
        },
        filter: {
          searchKeyword: typeof value === 'string' ? value : '',
          type: Array.isArray(type) ? type : typeof type === 'string' && type ? [type] : [],
          tags: Array.isArray(segmentTags) ? segmentTags : []
        }
      }
      const response = await api.post(`${segmentsListUrl}/search`, segmentPayload)

      setSegmentListData(response?.data?.data?.segments || [])
      setTotalSegments(response?.data?.data?.total || 0)
      //     if (selectedSegment || segmentName) {
      //   setSelectedSegment('')
      //   setSegmentName('')
      // }
      // if (predefinedSegmentID || segmentName || segmentQuerySql || segmentQueryJson) {
      //   setPredefinedSegmentID('')
      //   setSegmentName('')
      //   setSegmentQuerySql('')
      //   setSegmentQueryJson('')
      //   setTags([])
      // }
      // State clearing removed to prevent side effects during list refreshes
    } catch {
      // console.error('Error fetching segment list:', error)
      // const errorMessage = error.response?.data?.message || t('An unexpected error occurred')
      // showError(errorMessage)
      // // If the error is related to authentication, log out the user
      // if (error.response?.status === 401) {
      //   logout()
      // }
    }
  }

  const cloneSegment = async (id, segmentCloneName) => {
    try {
      const inputData = {
        name: segmentCloneName
      }
      const response = await api.post(`${segmentsListUrl}/${id}/clone`, inputData)

      if (response?.status === 200 || response?.status === 201) {
        showSuccess('Segment cloned successfully')
      }
    } catch {}
  }

  const getSegmentSelectlist = async () => {
    const response = await api.get(predefinedSegments)

    setSegmentSelectionData(response?.data?.data)
  }

  const createSegment = async () => {
    try {
      const inputData = {
        predefined_segment_id: predefinedSegmentID,
        name: segmentName,
        prompt: segmentQueryPrompt,
        query: segmentQuerySql,
        type: segmentType,
        query_json: segmentQueryJson,
        tags: tags
      }

      const response = await api.post(segmentsListUrl, inputData)

      if (response?.status === 200 || response?.status === 201) {
        showSuccess('Segment created successfully')
        if (predefinedSegmentID || segmentName || segmentQuerySql || segmentQueryJson || tags) {
          setPredefinedSegmentID('')
          setSegmentName('')
          setSegmentQuerySql('')
          setSegmentQueryJson('')
          setTags([])
        }
        router.push('/customer-data-platform/segment')
        resetSegmentState()
      }
    } catch {}
  }

  const getSegmentWithCampaigns = async id => {
    try {
      const response = await api.get(`${segmentsListUrl}/${id}`, { params: { includeCampaigns: true } })
      return response?.data?.data || null
    } catch {
      return null
    }
  }

  const getSegmentData = async id => {
    setSelectedSegmentID(id)

    const response = await api.get(`${segmentsListUrl}/${id}`)
    setSelectedSegment(response?.data?.data)
    setSelectedSegmentID(id)
    setSegmentType(response?.data?.data?.type)
    setPredefinedSegmentID(response?.data?.data?.predefined_segment_id)
  }

  /**
   * Generates a segment query using the provided prompt by calling the backend API.
   * @param {String} prompt: The prompt to generate the segment query.
   * @returns {Object|null} The generated segment query or null if an error occurs.
   */
  const generateSegmentQuery = async prompt => {
    try {
      const response = await api.post(generateSegmentQueryUrl, { prompt })

      if (response?.data?.data?.query) {
        const generatedQuery = response?.data?.data
        return generatedQuery
      } else {
        return null
      }
    } catch {
      return null
    }
  }

  const getSegmentFilterList = async () => {
    try {
      const response = await api.get(`${filterUrl}/segments`)

      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasTagFilter(!!tagsFilter)
      setSegmentFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])

      setSegmentOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {
      setSegmentFilterList([])
      setSegmentOtherFilterList([])
      return {}
    }
  }

  const editSegment = async id => {
    try {
      const inputData = {
        predefined_segment_id: predefinedSegmentID,
        name: segmentName,
        prompt: segmentQueryPrompt,
        query: segmentQuerySql,
        query_json: segmentQueryJson,
        tags: tags
      }

      // Determine the segment id to edit. Prefer the explicit selectedSegmentID state,
      // then common id fields on selectedSegment, then the passed-in `id` parameter.
      const targetId =
        selectedSegmentID || selectedSegment?.segment_id || selectedSegment?.segmentId || selectedSegment?.id || id

      await api.put(`${segmentsListUrl}/${targetId}`, inputData)
      showSuccess('Segment edited successfully')

      if (predefinedSegmentID || segmentName || segmentQuerySql || segmentQueryJson || tags) {
        setPredefinedSegmentID('')
        setSegmentName('')
        setSegmentQueryPrompt('')
        setSegmentQuerySql('')
        setSegmentQueryJson('')
        setTags([])
      }
      router.push('/customer-data-platform/segment')
    } catch {}
  }

  const refreshSegment = async id => {
    try {
      await api.get(`${segmentsListUrl}/${id}/audience-refresh`)
      showSuccess('Segment refreshed successfully')
      return true
    } catch {}
  }

  const checkSegmentUsage = async selectedIDList => {
    const validSegmentIds = (selectedIDList || []).map(id => String(id))

    if (validSegmentIds.length === 0) {
      return { safeToDelete: [], inUse: [], hasInUse: false }
    }

    try {
      // Call the check-usage endpoint which ONLY checks without deleting
      const inputData = {
        segmentIds: validSegmentIds,
        action: 'delete'
      }

      const response = await api.post(`${segmentsListUrl}/check-usage`, inputData)

      if (response?.status === 200 || response?.status === 204) {
        const result = response?.data?.data

        // Prepare usage data with segment names
        const inUseDetails = (result?.inUse || []).map(d => ({
          segmentId: d.segmentId,
          segmentName: getSegmentNameById(d.segmentId),
          campaignNames: d.campaignNames,
          campaignCount: d.campaignNames?.length || 0
        }))

        const safeToDelete = (result?.successIds || []).map(id => ({
          segmentId: id,
          segmentName: getSegmentNameById(id)
        }))

        return {
          safeToDelete,
          inUse: inUseDetails,
          hasInUse: inUseDetails.length > 0
        }
      }

      return { safeToDelete: [], inUse: [], hasInUse: false }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error'
      showError(`Failed to check segment usage: ${errorMsg}`)
      // Return empty result (fail closed - don't allow deletion if we can't verify)
      return { safeToDelete: [], inUse: [], hasInUse: true }
    }
  }

  const segmentBulkAction = async (selectedIDList, bulkAction, safeToDeleteIds = null) => {
    const validSegmentIds = safeToDeleteIds || (selectedIDList || []).map(id => String(id))

    if (validSegmentIds.length === 0) {
      showError('No segment IDs selected')
      return { deletedIds: [], failedIds: [] }
    }

    try {
      const inputData = {
        segmentIds: validSegmentIds,
        action: bulkAction
      }
      const response = await api.put(`${segmentsListUrl}/status`, inputData)
      if (response?.status === 200 || response?.status === 204) {
        const result = response?.data?.data || {}
        return {
          deletedIds: result.successIds || [],
          failedIds: result.inUseIds || []
        }
      }
      return { deletedIds: response?.data?.data?.successIds || [], failedIds: response?.data?.data?.inUseIds || [] }
    } catch (error) {
      throw error
    }
  }

  const formatToDateString = dateString => {
    if (dateString === null) {
      return ''
    } else {
      const date = new Date(dateString)

      const year = date.getFullYear()

      const month = String(date.getMonth() + 1).padStart(2, '0')

      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    }
  }
  const resetSegmentState = () => {
    setSelectedSegment({})
    setSelectedSegmentID('')
    setPredefinedSegmentID('')
    setSegmentName('')
    setSegmentQueryPrompt('')
    setSegmentQuerySql('')
    setSegmentQueryJson(null)
    setQueryData('')
    setSegmentType('dynamic')
    setTags([])
    setExecuteQueryData('')
  }

  const values = {
    tagListData,
    setTagListData,
    getTag: getTag,
    getSegmentList: getSegmentList,
    segmentListData,
    setSegmentListData,
    totalSegments,
    setTotalSegments,
    segmentSelectionData,
    setSegmentSelectionData,
    segmentName,
    setSegmentName,
    segmentQueryPrompt,
    setSegmentQueryPrompt,
    segmentQuerySql,
    setSegmentQuerySql,
    segmentQueryJson,
    setSegmentQueryJson,
    isResponsePending,
    setIsResponsePending,
    createSegment: createSegment,
    selectedSegment,
    setSelectedSegment,
    getSegmentData: getSegmentData,
    editSegment: editSegment,
    queryData,
    setQueryData,
    formatToDateString,
    getSegmentSelectlist: getSegmentSelectlist,
    setPredefinedSegmentID,
    predefinedSegmentID,
    executeQuery: executeQuery,
    executeQueryData,
    setExecuteQueryData,
    segmentType,
    setSegmentType,
    tags,
    setTags,
    refreshSegment: refreshSegment,
    segmentBulkAction,
    checkSegmentUsage: checkSegmentUsage,
    resetSegmentState: resetSegmentState,
    getSegmentFilterList: getSegmentFilterList,
    segmentFilterList,
    setSegmentFilterList,
    segmentOtherFilterList,
    setSegmentOtherFilterList,
    hasTagFilter,
    setHasTagFilter,
    cloneSegment: cloneSegment,
    isExecuting,
    setIsExecuting,
    generateSegmentQuery: generateSegmentQuery,
    getSegmentWithCampaigns
  }

  return <SegmentContext.Provider value={values}>{children}</SegmentContext.Provider>
}

export { SegmentContext, SegmentProvider }
